import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Spinner } from "react-bootstrap";
import { Settings2 } from "lucide-react";
import { techStacksList } from "../../utils/common";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import { AppButton, reactSelectTheme } from "../ui";

const defaultTechStacks = techStacksList.filter((t) => t !== "");
const difficulties = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

/* ── Converted to Tailwind utilities in Phase 5 (2026-08-24) ──────────────────
 * AIQuestionForm.scss is deleted (TOKEN_CONTRACT §6.1). Four literals in it and four
 * more in the react-select object below, and every one was the same mistake:
 *
 *   .ai-form__required      #ef4444           -> --danger
 *   .ai-form__manage-link   #3b82f6 / #2563eb -> --primary (it is a link, not a hue)
 *   .ai-form__textarea:focus #3b82f6 + rgba(59,130,246,.1) -> --primary
 *   themeStyles             #3b82f6 x3, #fff  -> ui/reactSelectTheme.js
 *
 * The focus ring is the one worth naming: the border and the glow were BOTH the v1
 * blue, so on a violet-branded institute every field in this form focused blue while
 * the buttons beside it were violet.
 *
 * The react-select object was one of THREE near-identical private copies (here,
 * GenerateAIQuestionsModal, TagSelector) that had drifted in four places. It is
 * `ui/reactSelectTheme.js` now — react-select styles through JS, so no colour gate in
 * this repo can see it, which is exactly why it drifted unnoticed.
 */
const themeStyles = reactSelectTheme();

const GROUP = "tw-flex tw-flex-col tw-gap-1.5 tw-flex-1 tw-min-w-0";
const LABEL = "tw-text-sm tw-font-medium tw-text-content tw-m-0";
const TEXTAREA =
  "tw-w-full tw-px-3 tw-py-2.5 tw-border tw-border-[var(--input-border)] tw-rounded-sm " +
  "tw-bg-[var(--input-bg)] tw-text-[var(--input-text)] tw-text-[0.9rem] tw-leading-[1.5] " +
  "tw-outline-none tw-resize-y tw-min-h-[110px] tw-font-[inherit] " +
  "tw-transition-[border-color,box-shadow] placeholder:tw-text-content-subtle " +
  "focus:tw-border-primary focus:tw-shadow-[0_0_0_3px_hsl(var(--primary)/0.1)]";

const toOption = (v) => (v ? { value: v, label: v } : null);

const AIQuestionForm = ({ form, setForm, loading, onSubmit, isEdit = false }) => {
  const [techStacks, setTechStacks] = useState(defaultTechStacks);
  const [stacksLoading, setStacksLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStacksLoading(true);
    apiClient
      .get("/ai/ai-questions/tech-stacks")
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.techStacks;
        if (Array.isArray(list) && list.length > 0) setTechStacks(list);
      })
      .catch(() => {
        /* fall back to defaults */
      })
      .finally(() => {
        if (!cancelled) setStacksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Always include the currently selected techStack, even if it's a legacy
  // value not in the institute's category list.
  const techStackOptions = (() => {
    const set = new Set(techStacks);
    if (form.techStack) set.add(form.techStack);
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ value: t, label: t }));
  })();

  return (
    <form className="tw-flex tw-flex-col tw-gap-5" onSubmit={onSubmit}>
      {/* Question */}
      <div className={GROUP}>
        <label htmlFor="ai-q-question" className={LABEL}>
          Question <span className="tw-text-danger tw-ml-0.5">*</span>
        </label>
        <textarea
          id="ai-q-question"
          className={TEXTAREA}
          rows={5}
          value={form.question}
          onChange={(e) => handleField("question", e.target.value)}
          placeholder="Enter the AI interview question..."
          required
        />
        <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
          Tip: write clear, scoped questions. Avoid multi-part questions in a single entry.
        </p>
      </div>

      {/* Category + Difficulty */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-4 max-[640px]:tw-grid-cols-1">
        <div className={GROUP}>
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <label htmlFor="ai-q-category" className={LABEL}>
              Category <span className="tw-text-danger tw-ml-0.5">*</span>
            </label>
            <Link to="/ai-bank/categories" className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[0.78rem] tw-font-medium tw-text-primary tw-no-underline hover:tw-underline">
              <Settings2 size={13} /> Manage categories
            </Link>
          </div>
          <Select
            inputId="ai-q-category"
            classNamePrefix="react-select"
            options={techStackOptions}
            value={toOption(form.techStack)}
            onChange={(opt) => handleField("techStack", opt?.value || "")}
            placeholder={stacksLoading ? "Loading..." : "Search or select a category..."}
            isClearable
            isSearchable
            isLoading={stacksLoading}
            styles={themeStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            noOptionsMessage={() => "No matching category. Add new ones from Manage categories."}
          />
        </div>

        <div className={GROUP}>
          <label htmlFor="ai-q-difficulty" className={LABEL}>
            Difficulty
          </label>
          <Select
            inputId="ai-q-difficulty"
            classNamePrefix="react-select"
            options={difficulties}
            value={difficulties.find((d) => d.value === form.difficulty) || difficulties[0]}
            onChange={(opt) => handleField("difficulty", opt?.value || "easy")}
            isSearchable={false}
            styles={themeStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>

      {can("ai.manage") && (
        <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-line">
          <AppButton type="submit" variant="default" disabled={loading || !form.techStack}>
            {loading ? (
              <>
                <Spinner size="sm" animation="border" /> Saving...
              </>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Create Question"
            )}
          </AppButton>
        </div>
      )}
    </form>
  );
};

export default AIQuestionForm;

